import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiLogOut, FiMenu, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { items } from "./DataSideBar";
import { MenuItem, ITEM_GAP, type MenuItemBadge } from "./MenuItem/MenuItem";
import { EXPANDED, MOBILE_QUERY, RAIL } from "./constants";
import { SidebarChromeContext, type SidebarChromeValue } from "./SidebarChromeContext";
import { ThemeToggle } from "../../components/dashboard/ThemeToggle";
import { useSubscription, ROUTE_FEATURE_MAP } from "../../contexts/SubscriptionContext";
import { UpgradeModal } from "../../components/FeatureGate/FeatureGate";
import logoHorizontal from "../../assets/png/Logo_CodeNekt_for_Fleet_Horizontal.png";
import logoIcon from "../../assets/png/CodeNekt_for_Fleet_Icon.png";
import { useGetOneCompanie } from "../../api/companies";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { Z } from "../../styles/zIndex";

/** Remembers the pinned choice across navigations and reloads. */
const PIN_STORAGE_KEY = "sidebar.pinned";
/**
 * Hover-to-expand intent delay (ms).
 *
 * With no delay, merely crossing the left edge of the screen on the way to
 * something else threw a 248px panel and its drop shadow over the page. Short
 * enough to feel instant when you mean it, long enough to ignore transit.
 */
const HOVER_OPEN_DELAY = 120;

const readPinned = (): boolean => {
  try {
    return localStorage.getItem(PIN_STORAGE_KEY) === "1";
  } catch {
    return false; // private mode / storage disabled
  }
};

const firstSegment = (path?: string) => (path ? "/" + path.split("/").filter(Boolean)[0] : "");

/**
 * Which subscription feature an entry needs, from the one table that answers
 * that question for the whole app.
 *
 * `ROUTE_FEATURE_MAP` is the shared source; `requiredFeature` on a menu item
 * is an override for entries that need a gate their route does not have. Both
 * the rendered lock state and the click handler call this, so the badge can
 * never disagree with what happens when you click.
 */
const resolveFeature = (to?: string, requiredFeature?: string): string | undefined =>
  requiredFeature ?? (to ? ROUTE_FEATURE_MAP[to] : undefined);

/**
 * The navigation panel, mounted ONCE for the whole authenticated app.
 *
 * WHY THIS COMPONENT EXISTS SEPARATELY FROM `Sidebar`
 * ---------------------------------------------------
 * Every page used to render its own `<Sidebar>`, panel included. React
 * therefore tore the entire panel down and rebuilt it on every navigation,
 * which cost us four things that all looked like unrelated bugs:
 *
 *   1. `useGetOneCompanie` refetched the company on every single page change.
 *   2. The nav list's scroll position reset, so on a laptop — where 16 items
 *      do not fit — you were thrown back to the top after each click and had
 *      to scroll down again to reach the item next to the one you just used.
 *   3. The active-strip `layoutId` animation could never play: framer-motion
 *      needs the previous element to still exist to animate from, and it had
 *      been unmounted. The strip teleported instead of sliding.
 *   4. Hover/pin state and the width spring restarted mid-flight, so
 *      navigating while the panel was expanding produced a visible jump.
 *
 * Mounting it from a layout route fixes all four at the source: React Router
 * keeps a layout element mounted while only the `<Outlet />` below it swaps.
 * Pages keep rendering `<Sidebar>` — that is now just the spacer + content
 * shell, and it reads the panel's state from context.
 */
export const SidebarChromeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { companie } = useGetOneCompanie(getCurrentFleetId());
  const { hasFeature, requiresUpgrade, featureLabel } = useSubscription();
  const [upgradeModal, setUpgradeModal] = useState<{ open: boolean; plan: string | null; feature: string }>({ open: false, plan: null, feature: "" });

  const reduceMotion = useReducedMotion();

  /*
   * `noSsr` evaluates the query during the first render instead of after it.
   * Without it the hook returns `false` on mount, so a phone painted the
   * desktop rail for one frame and then swapped to the drawer — a visible
   * flash on every hard load, plus a wasted layout pass.
   */
  const isMobile = useMediaQuery(MOBILE_QUERY, { noSsr: true });

  const [pinned, setPinned] = useState(readPinned);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  /** Where focus came from when the drawer opened, so it can be given back. */
  const returnFocusRef = useRef<HTMLElement | null>(null);

  /* ── Hover intent ──────────────────────────────────────────────────── */
  const hoverTimer = useRef<number | null>(null);
  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);
  const handleMouseEnter = useCallback(() => {
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => setHovered(true), HOVER_OPEN_DELAY);
  }, [clearHoverTimer]);
  const handleMouseLeave = useCallback(() => {
    clearHoverTimer();
    setHovered(false);
  }, [clearHoverTimer]);
  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  /* Scroll affordances for the nav list. The list overflows on any laptop
     screen (16 items at 44px is ~770px of content), and a 4px near-transparent
     thumb gave no sign of it, so the items past the fold read as missing
     rather than scrolled away. */
  const navScrollRef = useRef<HTMLUListElement | null>(null);
  const [scrollHint, setScrollHint] = useState({ top: false, bottom: false });

  const syncScrollHint = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setScrollHint({
      top: scrollTop > 4,
      // 4px slack: fractional layout heights otherwise leave the bottom fade
      // showing permanently on a list that is already fully scrolled.
      bottom: scrollTop + clientHeight < scrollHeight - 4,
    });
  }, []);

  /*
   * Toggling the pin.
   *
   * THE COLLAPSE BUG. `expanded` was `pinned || hovered`, and the toggle only
   * cleared `pinned`. To click the button you must be hovering the panel, so
   * `hovered` was always true at that moment and the panel stayed open — the
   * button appeared completely dead. It only "worked" once the pointer left.
   *
   * Clearing `hovered` alongside makes the collapse immediate, and because
   * mouseenter does not re-fire while the pointer is already inside, the panel
   * stays collapsed until the user actually leaves and comes back. The pending
   * hover timer has to go too, or it would re-open the panel ~120ms later.
   */
  const togglePinned = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      if (!next) {
        clearHoverTimer();
        setHovered(false);
      }
      try {
        localStorage.setItem(PIN_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* preference is best-effort */
      }
      return next;
    });
  }, [clearHoverTimer]);

  // On desktop the panel expands when pinned or hovered.
  // On mobile it is either fully open (drawer) or fully closed.
  const expanded = isMobile ? mobileOpen : pinned || hovered;
  const width = expanded ? EXPANDED : RAIL;

  // Reset the broken-image fallback when the logo URL changes.
  useEffect(() => setLogoError(false), [companie?.logo]);

  /*
   * Keep the scroll fades honest.
   *
   * Recomputed on resize and whenever the list's own box changes, not just on
   * scroll: collapsing the rail changes item widths and the PRO/BIENTÔT badges
   * disappear, so a list that overflowed when expanded may not when collapsed.
   * A ResizeObserver catches that without coupling this to every state that
   * can affect layout.
   */
  useLayoutEffect(() => {
    syncScrollHint();

    const el = navScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(syncScrollHint);
    ro.observe(el);
    window.addEventListener("resize", syncScrollHint);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncScrollHint);
    };
  }, [syncScrollHint, expanded, mobileOpen]);

  // Close the mobile drawer whenever the route changes (tap a nav item → navigate → close).
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [pathname, isMobile]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (isMobile && mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, mobileOpen]);

  /*
   * Escape closes the drawer, and Tab stays inside it.
   *
   * It is a modal surface — backdrop, scroll lock, `aria-modal` — and every
   * other modal in the app closes on Escape. Without it the only way out was
   * to hit the small X or tap the backdrop, which is not reachable by
   * keyboard at all.
   *
   * The trap matters just as much: `aria-modal` tells a screen reader the rest
   * of the page is inert, but it does not stop the Tab key. Focus would walk
   * off the last menu item into the page behind the backdrop, where the user
   * is tabbing through controls they cannot see and cannot click.
   */
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;

    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        return;
      }
      if (e.key !== "Tab") return;

      const panelEl = panelRef.current;
      if (!panelEl) return;

      /* Queried on each Tab rather than cached: the list is virtual-free but
         its contents change with `expanded`, locks and feature loading. */
      const focusables = Array.from(
        panelEl.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      /* Also catches focus having escaped already (active outside the panel),
         which is what happens on the very first Tab after a backdrop click. */
      if (!e.shiftKey && (active === last || !panelEl.contains(active))) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || !panelEl.contains(active))) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile, mobileOpen]);

  /*
   * Focus follows the drawer.
   *
   * Opening it left focus on the hamburger, behind the backdrop, so a keyboard
   * or screen-reader user opened a menu they were not in and then tabbed
   * through the page underneath. Focus moves to the first control inside and
   * returns to the trigger on close.
   */
  useEffect(() => {
    if (!isMobile) return;
    if (mobileOpen) {
      returnFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      // Wait a frame: the drawer mounts inside AnimatePresence.
      const id = window.requestAnimationFrame(() => {
        const first = panelRef.current?.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex="0"]'
        );
        first?.focus();
      });
      return () => window.cancelAnimationFrame(id);
    }
    returnFocusRef.current?.focus?.();
    returnFocusRef.current = null;
  }, [isMobile, mobileOpen]);

  // Logout is rendered in the footer, not as a nav item. Filter it explicitly
  // instead of slicing by index so adding a menu entry can't silently drop
  // the last items (a previous `items.slice(0, 12)` did exactly that).
  const navItems = items.filter((item) => item.iconName !== "logout");

  /*
   * `localStorage.clear()` also wiped `lang` and the theme mode, which are
   * preferences rather than credentials -- logging out reset the UI to
   * English/light every time. Only the two auth keys are removed.
   *
   * Verified against the keys the app actually writes: currentUser, lang,
   * token, plus ThemeModeContext's STORAGE_KEY. If an auth key is ever added,
   * it must be added here too.
   */
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("currentUser");
    window?.location?.reload();
  };
  const showLogo = !!companie?.logo && !logoError;

  const isActive = (to?: string) =>
    !!to && firstSegment(to) !== "/" && firstSegment(pathname) === firstSegment(to);

  const go = (to?: string, requiredFeature?: string) => {
    if (!to) return;
    const feature = resolveFeature(to, requiredFeature);
    if (feature && !hasFeature(feature)) {
      // `plan` may be null when the server has not told us which offer sells
      // this feature; the modal then shows a generic prompt rather than naming
      // a tier that may have been renamed or retired in the admin.
      const plan = requiresUpgrade(feature);
      setUpgradeModal({ open: true, plan, feature: featureLabel(feature) });
      return;
    }
    navigate(to);
    if (isMobile) setMobileOpen(false);
  };

  const soonLabel = t("sidebar.comingSoon", "Bientôt disponible");
  const proLabel = t("sidebar.proFeature", "Inclus dans une offre supérieure");

  /*
   * Tooltip wording is composed here, where `t` lives, and handed to MenuItem
   * as a finished string.
   *
   * Two bugs this closes. The Sidebar used to wrap coming-soon rows in its own
   * Tooltip while MenuItem wrapped collapsed rows in another — two tooltips on
   * one element, fighting over the same hover, one winning arbitrarily. And
   * the coming-soon tooltip said only "Bientôt disponible", so in the rail you
   * got the status of an item whose name you could not see.
   */
  const tooltipFor = (label: string, locked: boolean, soon: boolean): string | undefined => {
    if (soon) return expanded ? soonLabel : `${label} — ${soonLabel}`;
    if (expanded) return undefined; // the label is already on screen
    return locked ? `${label} — ${proLabel}` : label;
  };

  /* The page shell re-renders on every navigation; this value must not make it
     re-render for any other reason. */
  const chrome = useMemo<SidebarChromeValue>(() => ({ pinned, isMobile }), [pinned, isMobile]);

  // ── The rail/panel itself (shared by desktop + mobile) ──────────────
  const panel = (
    <Box
      ref={panelRef}
      component={motion.div}
      // Hover-expand only makes sense with a real pointer (desktop).
      onMouseEnter={isMobile ? undefined : handleMouseEnter}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
      role={isMobile ? "dialog" : undefined}
      aria-modal={isMobile ? true : undefined}
      aria-label={isMobile ? t("sidebar.mainNav", "Navigation principale") : undefined}
      initial={false}
      animate={{ width: isMobile ? EXPANDED : width }}
      transition={
        reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 320, damping: 32 }
      }
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        /*
         * `var(--app-vh)` resolves to 100dvh where supported, 100vh otherwise.
         *
         * This was `100vh`, which on Android Chrome measures the viewport as
         * if the URL bar were retracted. With the URL bar visible the panel
         * ran ~60-110px past the bottom of the screen, and since the footer
         * (theme toggle + logout) is the last thing in this column, logout sat
         * in that overhang. It could not be scrolled to either: the panel is
         * `position: fixed`, and the drawer locks body scroll while open.
         */
        height: "var(--app-vh)",
        minWidth: isMobile ? EXPANDED : RAIL,
        /* Was 1300 -- identical to MUI's Modal default, so the sidebar and any
           dialog were tied and resolved only by DOM order. */
        zIndex: Z.SIDEBAR,
        backgroundColor: "var(--c-nav)",
        borderRight: "1px solid rgba(148,163,184,0.12)",
        display: "flex",
        flexDirection: "column",
        py: 2,
        overflowX: "hidden",
        /* The nav list below is the scroller (`flex: 1; overflowY: auto`).
           Leaving this `visible` let content spill outside a fixed-height
           fixed-position box, where it is unreachable by any scroll. */
        overflowY: "hidden",
        boxShadow: expanded ? "12px 0 32px rgba(2,6,23,0.45)" : "none",
        willChange: "width, transform",
      }}
    >
      {/* ── Brand / logo ─────────────────────────────────────────────── */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          px: expanded ? 2 : 0,
          justifyContent: expanded ? "flex-start" : "center",
          mb: 2,
          minHeight: 44,
          flexShrink: 0,
          flexWrap: "nowrap",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: expanded && !showLogo ? "none" : "grid",
            placeItems: "center",
            background: showLogo ? "rgba(96,165,250,0.14)" : "transparent",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {showLogo ? (
            <img
              src={companie!.logo}
              alt=""
              onError={() => setLogoError(true)}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <img src={logoIcon} alt="CodeNekt for Fleet" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          )}
        </Box>
        {expanded && (
          <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduceMotion ? 0 : 0.18 }}
            sx={{ overflow: "hidden", minWidth: 0, flex: 1, display: "flex", alignItems: "center" }}
          >
            {showLogo ? (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "var(--c-nav-text)",
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {companie?.name ?? "CodeNekt"}
                </Typography>
                <Typography sx={{ color: "var(--c-nav-muted)", fontSize: 11, whiteSpace: "nowrap" }}>
                  {t("dashboard.fleetSuite", "Fleet Suite")}
                </Typography>
              </Box>
            ) : (
              <img
                src={logoHorizontal}
                alt="CodeNekt for Fleet"
                style={{ height: 28, width: "auto", maxWidth: "100%", objectFit: "contain" }}
              />
            )}
          </Box>
        )}
        {/* Close affordance inside the mobile drawer */}
        {isMobile && expanded && (
          <IconButton
            aria-label={t("common.close", "Close")}
            onClick={() => setMobileOpen(false)}
            sx={{ color: "var(--c-nav-muted)", "&:hover": { color: "var(--c-nav-text)" } }}
          >
            <FiX size={18} />
          </IconButton>
        )}
      </Stack>

      {/* ── Pin / collapse toggle (desktop only) ──────────────────────── */}
      {!isMobile && (
        <Stack
          direction="row"
          justifyContent={expanded ? "flex-end" : "center"}
          sx={{ px: expanded ? 1.5 : 0, mb: 1, flexShrink: 0 }}
        >
          <Tooltip
            title={pinned ? t("sidebar.collapse", "Collapse") : t("sidebar.expand", "Expand")}
            placement="right"
          >
            <Box
              role="button"
              tabIndex={0}
              aria-label={pinned ? t("sidebar.collapse", "Collapse") : t("sidebar.expand", "Expand")}
              aria-pressed={pinned}
              onClick={togglePinned}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  togglePinned();
                }
              }}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                color: "var(--c-nav-muted)",
                transition: "background-color 150ms ease, color 150ms ease",
                "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
                "&:hover": {
                  backgroundColor: "rgba(96,165,250,0.12)",
                  color: "var(--c-nav-text)",
                },
              }}
            >
              {pinned ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />}
            </Box>
          </Tooltip>
        </Stack>
      )}

      {/* ── Nav items ─────────────────────────────────────────────────── */}
      {/*
        <nav> hosts the scroll fades. They are painted over the list rather
        than inside it so they stay put while it scrolls.
      */}
      <Box
        component="nav"
        aria-label={t("sidebar.mainNav", "Navigation principale")}
        sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}
      >
        <Stack
          component="ul"
          ref={navScrollRef}
          onScroll={syncScrollHint}
          sx={{
            flex: 1,
            minHeight: 0,
            m: 0,
            px: 0,
            py: 1,
            listStyle: "none",
            overflowY: "auto",
            overflowX: "hidden",
            /* Scrolling past either end of the menu was chaining into the page
               behind it, which on a phone closes the drawer's illusion. */
            overscrollBehavior: "contain",
            /*
             * The list owns the vertical rhythm, the items do not.
             *
             * This was `spacing={0.5}`, which MUI applies as a margin on the
             * sibling BELOW — so the gap depended on what each item happened
             * to render as (bare row when expanded, tooltip wrapper when
             * collapsed, an extra Box for coming-soon entries). `rowGap` is a
             * property of the container, so every child gets the same gap
             * whatever its wrapper.
             */
            rowGap: `${ITEM_GAP}px`,
            /*
             * Safety net for the squeeze described in MenuItem.tsx: whatever a
             * child turns out to be, it keeps its natural height and the list
             * scrolls instead. Without this the rail and the expanded panel
             * drift apart again the moment a new wrapper appears.
             */
            "& > *": { flexShrink: 0 },
            /*
             * A discoverable scrollbar.
             *
             * This was 4px wide with a 20%-opacity thumb, which on a dark
             * panel is invisible — so the items below the fold looked absent,
             * not scrolled. `scrollbar-gutter: stable` reserves the track in
             * both states so item alignment does not shift by a few pixels
             * when the list stops overflowing.
             */
            scrollbarGutter: "stable",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(148,163,184,0.45) transparent",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 8,
              backgroundColor: "rgba(148,163,184,0.35)",
              border: "2px solid transparent",
              backgroundClip: "content-box",
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(148,163,184,0.6)",
            },
          }}
        >
          {navItems.map((item) => {
            /*
             * THE PHANTOM-UNLOCKED ROW.
             *
             * `isLocked` used to read only `item.requiredFeature`, while the
             * click handler fell back to ROUTE_FEATURE_MAP. AI Assistant is
             * gated in that map and nowhere else, so the row rendered with no
             * PRO badge and no dot -- it looked included -- and then answered
             * the click with an upgrade modal. Both now resolve the feature
             * the same way, so what the row shows is what the click does.
             */
            const feature = resolveFeature(item.to, item.requiredFeature);
            const isLocked = !!feature && !hasFeature(feature);
            const isComingSoon = !!item.comingSoon;
            const label = t(item.labelKey, item.labelDefault);

            const badge: MenuItemBadge | undefined = isComingSoon
              ? { label: t("sidebar.comingSoonBadge", "BIENTÔT"), tone: "soon" }
              : isLocked
              ? { label: "PRO", tone: "pro" }
              : undefined;

            return (
              <MenuItem
                /* Was `key={index}`: reordering or filtering the menu (which
                   `hasFeature` can do at any time) made React reuse the wrong
                   row's DOM and animation state. */
                key={item.labelKey}
                iconName={item.iconName}
                expanded={expanded}
                text={label}
                to={item.to}
                width={item.width}
                height={item.height}
                active={isActive(item.to)}
                locked={isLocked}
                comingSoon={isComingSoon}
                badge={badge}
                tooltip={tooltipFor(label, isLocked, isComingSoon)}
                onClick={isComingSoon ? undefined : () => go(item.to, item.requiredFeature)}
              />
            );
          })}
        </Stack>

        {/* Fades: the only cue that survives on a trackpad, where the
            scrollbar is hidden until the moment you already started
            scrolling. Non-interactive so they never eat a click. */}
        {scrollHint.top && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 20,
              pointerEvents: "none",
              background: "linear-gradient(to bottom, var(--c-nav), transparent)",
            }}
          />
        )}
        {scrollHint.bottom && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 24,
              pointerEvents: "none",
              background: "linear-gradient(to top, var(--c-nav), transparent)",
            }}
          />
        )}
      </Box>

      {/* ── Footer: theme + logout ────────────────────────────────────── */}
      <Divider sx={{ borderColor: "rgba(148,163,184,0.12)", mx: 1.5, my: 1 }} />
      <Stack
        direction={expanded ? "row" : "column"}
        alignItems="center"
        justifyContent={expanded ? "space-between" : "center"}
        spacing={1}
        sx={{
          px: expanded ? 2 : 0,
          flexShrink: 0,
          /* Keeps logout clear of the Android gesture bar / iOS home
             indicator, which otherwise overlaps the last 16-34px. */
          pb: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <ThemeToggle variant="rail" />
        <Tooltip title={t("sidebar.logout", "Logout")} placement="right">
          <Box
            role="button"
            tabIndex={0}
            aria-label={t("sidebar.logout", "Logout")}
            onClick={handleLogout}
            /* Was a bare <div onClick>: not focusable, not operable by
               keyboard, and invisible to screen readers. */
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLogout();
              }
            }}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: "var(--c-nav-muted)",
              transition: "background-color 150ms ease, color 150ms ease",
              "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
              "&:hover": { backgroundColor: "rgba(239,68,68,0.16)", color: "#FCA5A5" },
            }}
          >
            <FiLogOut size={18} />
          </Box>
        </Tooltip>
      </Stack>
    </Box>
  );

  return (
    <SidebarChromeContext.Provider value={chrome}>
      {/* ── MOBILE: floating hamburger to open the drawer ── */}
      {isMobile && !mobileOpen && (
        <IconButton
          aria-label={t("sidebar.expand", "Open menu")}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
          sx={{
            position: "fixed",
            top: 12,
            left: 12,
            zIndex: Z.SIDEBAR_TRIGGER,
            width: 44,
            height: 44,
            borderRadius: 2,
            backgroundColor: "var(--c-nav)",
            color: "var(--c-nav-text)",
            boxShadow: "0 4px 14px rgba(2,6,23,0.35)",
            "&:hover": { backgroundColor: "var(--c-nav)" },
          }}
        >
          <FiMenu size={20} />
        </IconButton>
      )}

      {/* ── MOBILE: backdrop + slide-in drawer ── */}
      {isMobile ? (
        <AnimatePresence>
          {mobileOpen && (
            <>
              <Box
                component={motion.div}
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.2 }}
                onClick={() => setMobileOpen(false)}
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: Z.SIDEBAR_BACKDROP,
                  backgroundColor: "rgba(2,6,23,0.55)",
                  backdropFilter: "blur(2px)",
                }}
              />
              <Box
                component={motion.div}
                key="drawer"
                initial={{ x: -EXPANDED }}
                animate={{ x: 0 }}
                exit={{ x: -EXPANDED }}
                transition={
                  reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 360, damping: 36 }
                }
                sx={{ position: "fixed", top: 0, left: 0, zIndex: Z.SIDEBAR }}
              >
                {panel}
              </Box>
            </>
          )}
        </AnimatePresence>
      ) : (
        panel
      )}

      {children}

      <UpgradeModal
        open={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, plan: null, feature: "" })}
        requiredPlan={upgradeModal.plan}
        featureName={upgradeModal.feature}
      />
    </SidebarChromeContext.Provider>
  );
};
